import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DollarSign, Edit, Lock, UserPlus, UserX } from 'react-feather';
import DataTable from '../../../Common/DataTable';
import { Btn } from '../../../../AbstractElements';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import FileCloseModal from '../../Arbitrations/ArbitrationDetails/FileCloseModal';
import PaymentCollectionDataModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionDataModal';
import PaymentCollectionListModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionListModal';
import { fetchFormData, fetchTableData, assignArbitrationToSelf, releaseArbitrationFromSelf, patchArbitrationInformation, createFinalCompensation } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import { normalizeFieldValue, displayDecimal } from '../../../../_helper/regexHandler';

const TodoTabArbitrationActive = ({ onCountChange, onLoadingChange }) => {
    const navigate = useNavigate();
    const isInitialLoad = useRef(true);
    const searchRef = useRef({ caseNumberSearch: '', tcIdSearch: '', accidentDateSearch: '' });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [caseNumberSearch, setCaseNumberSearch] = useState('');
    const [tcIdSearch, setTcIdSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');

    const [arbitrationStates, setArbitrationStates] = useState([]);
    const [selectedState, setSelectedState] = useState('');

    const [isArbitrationExpenseModalOpen, setIsArbitrationExpenseModalOpen] = useState(false);
    const [currentArbitrationItem, setCurrentArbitrationItem] = useState(null);

    const [isFileCloseModalOpen, setIsFileCloseModalOpen] = useState(false);
    const [currentArbitrationIdForClose, setCurrentArbitrationIdForClose] = useState(null);

    const [isArbitrationInfoModalOpen, setIsArbitrationInfoModalOpen] = useState(false);
    const [currentArbitrationInfoItem, setCurrentArbitrationInfoItem] = useState(null);
    const [arbitrationApplicationDate, setArbitrationApplicationDate] = useState('');
    const [arbitrationCaseNumber, setArbitrationCaseNumber] = useState('');
    const [arbitrationPriceForNow, setArbitrationPriceForNow] = useState('');
    const [isSubmittingArbitrationInfo, setIsSubmittingArbitrationInfo] = useState(false);

    const [isAssigningArbitration, setIsAssigningArbitration] = useState(false);
    const [isReleasingArbitration, setIsReleasingArbitration] = useState(false);

    // Tahsilat state'leri
    const [isPaymentCollectionListModalOpen, setIsPaymentCollectionListModalOpen] = useState(false);
    const [paymentCollections, setPaymentCollections] = useState([]);
    const [isPaymentCollectionsLoading, setIsPaymentCollectionsLoading] = useState(false);
    const [currentAccidentFileForPayments, setCurrentAccidentFileForPayments] = useState(null);
    const [paymentCollectionModalOpen, setPaymentCollectionModalOpen] = useState(false);
    const [paymentCollectionModalMode, setPaymentCollectionModalMode] = useState('add');
    const [selectedPaymentCollection, setSelectedPaymentCollection] = useState(null);
    const [finalCompensationSubmittingId, setFinalCompensationSubmittingId] = useState(null);

    useEffect(() => {
        if (typeof onCountChange === 'function') {
            onCountChange(totalRows);
        }
    }, [onCountChange, totalRows]);

    useEffect(() => {
        if (typeof onLoadingChange === 'function') {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    useEffect(() => {
        searchRef.current = {
            caseNumberSearch,
            tcIdSearch,
            accidentDateSearch
        };
    }, [caseNumberSearch, tcIdSearch, accidentDateSearch]);

    useEffect(() => {
        const fetchArbitrationStates = async () => {
            try {
                const queryParams = new URLSearchParams();
                queryParams.append('isEnforcement', 'false');
                queryParams.append('page', '1');
                queryParams.append('pageSize', '100');

                const response = await fetchFormData({
                    apiEndpoint: process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ArbitrationState?' + queryParams.toString(),
                    page: 1,
                    pageSize: 100
                });

                // fetchFormData -> extractItemsFromResponse zaten düz array döner.
                const allItems = Array.isArray(response) ? response : [];
                setArbitrationStates(allItems);
            } catch (error) {
                toast.error('Tahkim durumları alınırken bir hata oluştu.');
                setArbitrationStates([]);
            }
        };

        fetchArbitrationStates();
    }, []);

    const paymentCollectionApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/paymentcollection';

    const fetchArbitrationActiveData = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('isEnforcement', 'false');

            const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
            if (hasCaseNumberSearch) {
                queryParams.append('caseNumber', caseNumberSearch.trim());
            }

            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;
            const hasAccidentDateSearch = accidentDateSearch && accidentDateSearch.trim().length > 0;

            if (!hasCaseNumberSearch) {
                if (hasTcIdSearch) {
                    queryParams.append('tcId', tcIdSearch.trim());
                }
                if (hasAccidentDateSearch) {
                    queryParams.append('accidentDate', accidentDateSearch);
                }
            }

            if (!hasCaseNumberSearch && !hasTcIdSearch && !hasAccidentDateSearch && selectedState && selectedState !== '') {
                queryParams.append('stateId', selectedState);
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('Tahkim verileri alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, caseNumberSearch, tcIdSearch, accidentDateSearch, selectedState]);

    const fetchArbitrationActiveDataNonSearch = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/Arbitration';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('isEnforcement', 'false');

            const { caseNumberSearch: currentCaseSearch, tcIdSearch: currentTcIdSearch, accidentDateSearch: currentAccidentDateSearch } = searchRef.current;

            const hasCaseNumberSearch = currentCaseSearch && currentCaseSearch.trim().length >= 3;
            if (hasCaseNumberSearch) {
                queryParams.append('caseNumber', currentCaseSearch.trim());
            }

            const hasTcIdSearch = currentTcIdSearch && currentTcIdSearch.trim().length >= 3;
            const hasAccidentDateSearch = currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0;

            if (!hasCaseNumberSearch) {
                if (hasTcIdSearch) {
                    queryParams.append('tcId', currentTcIdSearch.trim());
                }
                if (hasAccidentDateSearch) {
                    queryParams.append('accidentDate', currentAccidentDateSearch);
                }
            }

            if (!hasCaseNumberSearch && !hasTcIdSearch && !hasAccidentDateSearch && selectedState && selectedState !== '') {
                queryParams.append('stateId', selectedState);
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('Tahkim verileri alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, selectedState]);

    // Pagination ve non-search filtre (selectedState) değişiminde anında fetch
    useEffect(() => {
        fetchArbitrationActiveDataNonSearch();
        // fetchArbitrationActiveDataNonSearch zaten [page, pageSize, selectedState] deps'i ile yeniden oluşuyor; burada tekrar dep olarak eklemek çift fetch'e yol açar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, selectedState]);

    // Search input'ları için debounce'lu fetch; callback'ler dep olarak dahil edilmez — aksi halde non-search filtreler değiştiğinde de fire edip çift fetch olur (jitter).
    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        const hasAnySearch = caseNumberSearch.trim() || tcIdSearch.trim() || accidentDateSearch.trim();
        const timeoutId = setTimeout(() => {
            const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length > 0;
            const hasValidCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
            const hasValidTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;

            if (hasAnySearch) {
                if ((hasCaseNumberSearch && !hasValidCaseNumberSearch) || (hasTcIdSearch && !hasValidTcIdSearch)) {
                    return;
                }
                fetchArbitrationActiveData();
            } else {
                fetchArbitrationActiveDataNonSearch();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseNumberSearch, tcIdSearch, accidentDateSearch]);

    const handleCaseNumberSearchChange = (event) => {
        const inputValue = event.target.value;
        const normalizedValue = normalizeFieldValue('caseNumberSearch', inputValue);

        setCaseNumberSearch(normalizedValue);
        setPage(1);

        if (normalizedValue && normalizedValue.trim().length >= 3) {
            setTcIdSearch('');
            setAccidentDateSearch('');
            setSelectedState('');
        }
    };

    const handleTcIdSearchChange = (event) => {
        const inputValue = event.target.value;
        const numericValue = normalizeFieldValue('tcIdSearch', inputValue);

        setTcIdSearch(numericValue);
        setPage(1);

        if (numericValue && numericValue.trim().length > 0) {
            setCaseNumberSearch('');
            setSelectedState('');
        }
    };

    const handleAccidentDateSearchChange = (event) => {
        const dateValue = event.target.value;

        setAccidentDateSearch(dateValue);
        setPage(1);

        if (dateValue && dateValue.trim().length > 0) {
            setCaseNumberSearch('');
            setSelectedState('');
        }
    };

    const handleArbitrationStateFilterChange = (event) => {
        setSelectedState(event.target.value);
        setPage(1);
    };

    const handleArbitrationExpenseModal = (arbitrationItem) => {
        setCurrentArbitrationItem(arbitrationItem);
        setIsArbitrationExpenseModalOpen(true);
    };

    const closeArbitrationExpenseModal = () => {
        setIsArbitrationExpenseModalOpen(false);
        setCurrentArbitrationItem(null);
    };

    const handleFileCloseModal = (arbitrationItem) => {
        setCurrentArbitrationIdForClose(arbitrationItem.id);
        setIsFileCloseModalOpen(true);
    };

    const closeFileCloseModal = () => {
        setIsFileCloseModalOpen(false);
        setCurrentArbitrationIdForClose(null);
    };

    const openArbitrationInfoModal = (arbitrationItem) => {
        setCurrentArbitrationInfoItem(arbitrationItem);

        const arbInfo = arbitrationItem.arbitrationInformation;
        if (arbInfo) {
            if (arbInfo.applicationDate) {
                const date = new Date(arbInfo.applicationDate);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                setArbitrationApplicationDate(`${year}-${month}-${day}T${hours}:${minutes}`);
            } else {
                setArbitrationApplicationDate('');
            }
            setArbitrationCaseNumber(arbInfo.caseNumber || '');
            setArbitrationPriceForNow(arbInfo.priceForNow || '');
        } else {
            setArbitrationApplicationDate('');
            setArbitrationCaseNumber('');
            setArbitrationPriceForNow('');
        }

        setIsArbitrationInfoModalOpen(true);
    };

    const closeArbitrationInfoModal = () => {
        setIsArbitrationInfoModalOpen(false);
        setCurrentArbitrationInfoItem(null);
        setArbitrationApplicationDate('');
        setArbitrationCaseNumber('');
        setArbitrationPriceForNow('');
    };

    const handleArbitrationEdit = (arbitrationItem) => {
        navigate(`${process.env.PUBLIC_URL}/arbitration/${arbitrationItem.id}`);
    };

    const handleArbitrationAssignToSelf = async (arbitrationId) => {
        if (isAssigningArbitration) return;

        setIsAssigningArbitration(true);
        try {
            const responseData = await assignArbitrationToSelf({ arbitrationId });
            toast.success(responseData?.message || "Tahkim işi başarıyla kendinize atandı!");
            fetchArbitrationActiveData();
        } catch (error) {
            if (!error.response || error.response.status >= 500) {
                toast.error("Tahkim işi kendinize atanırken bir hata oluştu.");
            }
        } finally {
            setIsAssigningArbitration(false);
        }
    };

    const handleArbitrationReleaseFromSelf = async (arbitrationId) => {
        if (isReleasingArbitration) return;

        setIsReleasingArbitration(true);
        try {
            const responseData = await releaseArbitrationFromSelf({ arbitrationId });
            toast.success(responseData?.message || "Tahkim işi başarıyla kendinizden kaldırıldı!");
            fetchArbitrationActiveData();
        } catch (error) {
            if (!error.response || error.response.status >= 500) {
                toast.error("Tahkim işi kendinizden kaldırılırken bir hata oluştu.");
            }
        } finally {
            setIsReleasingArbitration(false);
        }
    };

    const handleSubmitArbitrationInfo = async () => {
        if (!currentArbitrationInfoItem?.arbitrationInformation?.id) {
            toast.error('Tahkim bilgi ID bulunamadı!');
            return;
        }

        if (!arbitrationApplicationDate || !arbitrationCaseNumber) {
            toast.error('Başvuru tarihi ve dava numarası zorunludur!');
            return;
        }

        setIsSubmittingArbitrationInfo(true);

        const payload = {
            applicationDate: new Date(arbitrationApplicationDate).toISOString(),
            caseNumber: arbitrationCaseNumber,
            priceForNow: parseFloat(arbitrationPriceForNow) || 0
        };

        try {
            await patchArbitrationInformation({
                arbitrationInformationId: currentArbitrationInfoItem.arbitrationInformation.id,
                payload
            });

            toast.success('Tahkim bilgileri başarıyla güncellendi!');
            closeArbitrationInfoModal();
            fetchArbitrationActiveData();
        } catch (error) {
            toast.error('Tahkim bilgileri güncellenirken hata oluştu!');
        } finally {
            setIsSubmittingArbitrationInfo(false);
        }
    };

    // ─── Tahsilat Handler'ları ───

    const fetchPaymentCollectionsForAccidentFile = useCallback(async (accidentFileId) => {
        if (!accidentFileId) { setPaymentCollections([]); return; }
        setIsPaymentCollectionsLoading(true);
        try {
            const response = await fetchFormData({
                apiEndpoint: `${paymentCollectionApiUrl}?AccidentFile=${encodeURIComponent(String(accidentFileId))}`,
                page: 1,
                pageSize: 100,
            });
            let collections = [];
            if (response?.isSuccess && Array.isArray(response?.data?.items)) collections = response.data.items;
            else if (Array.isArray(response?.items)) collections = response.items;
            else if (Array.isArray(response)) collections = response;
            else if (response?.data && Array.isArray(response.data)) collections = response.data;
            setPaymentCollections(collections);
        } catch (error) {
            if (shouldToastError(error)) toast.error('Tahsilatlar yüklenirken bir hata oluştu.');
            setPaymentCollections([]);
        } finally {
            setIsPaymentCollectionsLoading(false);
        }
    }, [paymentCollectionApiUrl]);

    const handleOpenPaymentCollectionListModal = (row) => {
        const accidentFile = row.accidentFile;
        setCurrentAccidentFileForPayments({
            ...accidentFile,
            _arbitrationId: row.id
        });
        fetchPaymentCollectionsForAccidentFile(accidentFile?.id);
        setIsPaymentCollectionListModalOpen(true);
    };

    const closePaymentCollectionListModal = () => {
        setIsPaymentCollectionListModalOpen(false);
        setPaymentCollectionModalOpen(false);
        setSelectedPaymentCollection(null);
        setPaymentCollections([]);
        setCurrentAccidentFileForPayments(null);
    };

    const handleAddPaymentCollection = () => {
        setSelectedPaymentCollection(null);
        setPaymentCollectionModalMode('add');
        setPaymentCollectionModalOpen(true);
    };

    const handleEditPaymentCollection = (collection) => {
        setSelectedPaymentCollection(collection);
        setPaymentCollectionModalMode('modify');
        setPaymentCollectionModalOpen(true);
    };

    const togglePaymentCollectionModal = () => {
        if (paymentCollectionModalOpen) {
            setPaymentCollectionModalOpen(false);
            setTimeout(() => setSelectedPaymentCollection(null), 300);
        } else {
            setPaymentCollectionModalOpen(true);
        }
    };

    const refreshPaymentCollections = useCallback(() => {
        if (currentAccidentFileForPayments?.id) {
            fetchPaymentCollectionsForAccidentFile(currentAccidentFileForPayments.id);
        }
    }, [fetchPaymentCollectionsForAccidentFile, currentAccidentFileForPayments]);

    const handleSendPaymentCollectionToCover = async (collection) => {
        const collectionId = collection?.id || collection?._id;
        if (!collectionId) { toast.warn('Tahsilat kaydı kimliği bulunamadı.'); return; }
        if (!currentAccidentFileForPayments?.id) { toast.warn('Kaza dosyası kimliği bulunamadı.'); return; }

        setFinalCompensationSubmittingId(collectionId);
        try {
            await createFinalCompensation({
                paymentCollections: [String(collectionId)],
                accidentFile: [String(currentAccidentFileForPayments.id)],
                arbitration: currentAccidentFileForPayments._arbitrationId ? [String(currentAccidentFileForPayments._arbitrationId)] : [],
                enforcement: []
            });
            await fetchPaymentCollectionsForAccidentFile(currentAccidentFileForPayments.id);
            fetchArbitrationActiveData();
        } catch {
            // Hata createFinalCompensation içinde handle ediliyor
        } finally {
            setFinalCompensationSubmittingId(null);
        }
    };

    const columns = useMemo(() => ([
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.accidentFile?.customer;
                if (!customer) return '-';
                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                }
                return customer.clientName || '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.accidentFile?.customer;
                if (!customer) return '-';
                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                }
                return customer.identityNumber || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.accidentFile?.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Esas Numarası',
            selector: row => row.arbitrationInformation?.caseNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accidentFile?.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accidentFile?.accident?.accidentDate ? new Date(row.accidentFile.accident.accidentDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.accidentFile?.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';

                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');

                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const amountWorked = row.accidentFile?.amountWorked;
                if (amountWorked) return `${amountWorked.toLocaleString('tr-TR')} ₺`;
                const filesToBeWorked = row.accidentFile?.filesToBeWorked || [];
                const total = filesToBeWorked.reduce((sum, file) => sum + (Number(file?.fileExpense) || 0), 0);
                return total > 0 ? `${total.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Başvuru Ücreti',
            selector: row => {
                const applicationFee = row.arbitrationInformation?.arbitrationApplicationFee;
                return applicationFee ? `${applicationFee.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kod',
            selector: row => row.arbitrationInformation?.code || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Tahkim Durumu',
            selector: row => row.arbitrationState?.title || '-',
            sortable: true,
            grow: 2.5,
            minWidth: '200px'
        },
        {
            name: 'Tahsilat',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'info',
                        size: 'sm',
                        outline: true,
                        onClick: () => handleOpenPaymentCollectionListModal(row),
                        title: 'Tahsilatları Görüntüle / Ekle'
                    }}
                >
                    <DollarSign size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        },
        {
            name: 'Kendine Ata/Kaldır',
            cell: (row) => {
                if (!row.appointedUser) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => handleArbitrationAssignToSelf(row.id),
                                title: 'Kendine Ata',
                                disabled: isAssigningArbitration
                            }}
                        >
                            <UserPlus size={18} />
                        </Btn>
                    );
                }

                if (row.canRelease) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-danger',
                                size: 'sm',
                                onClick: () => handleArbitrationReleaseFromSelf(row.id),
                                title: 'Kendinden Kaldır',
                                disabled: isReleasingArbitration
                            }}
                        >
                            <UserX size={18} />
                        </Btn>
                    );
                }

                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '150px',
        },
        {
            name: 'İşlemler',
            cell: row => {
                const arbitrationState = row.arbitrationState?.title;

                if (arbitrationState === 'Tahkim Başvuru Ücreti Ödeme Bekleniyor' && row.canEdit === true) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-success',
                                size: 'sm',
                                onClick: () => handleArbitrationExpenseModal(row),
                                title: 'Başvuru Ücreti'
                            }}
                        >
                            <DollarSign size={14} />
                        </Btn>
                    );
                }

                /* Tahkim Başvuru Bekleniyor durumu kaldırıldı — bilgiler Tahkime Hazırlar modalında girilmektedir
                if (arbitrationState === 'Tahkim Başvuru Bekleniyor' && row.canEdit === true) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-info',
                                size: 'sm',
                                onClick: () => openArbitrationInfoModal(row),
                                title: 'Tahkim Bilgileri Gir'
                            }}
                        >
                            <FileText size={14} />
                        </Btn>
                    );
                }
                */

                if (row.canEdit === true) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-warning',
                                size: 'sm',
                                onClick: () => handleArbitrationEdit(row),
                                title: 'Düzenle'
                            }}
                        >
                            <Edit size={14} />
                        </Btn>
                    );
                }
                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px',
        },
        {
            name: 'Kapat',
            cell: row => {
                const currentState = row.arbitrationState?.title;
                const shouldShow = !['Tekrar Başvuru Bekleniyor', 'Kapama Bekleniyor'].includes(currentState);

                if (shouldShow) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => handleFileCloseModal(row),
                                title: 'Dosya Kapat'
                            }}
                        >
                            <Lock size={14} />
                        </Btn>
                    );
                }
                return null;
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '80px',
        }
    ]), [handleArbitrationAssignToSelf, handleArbitrationReleaseFromSelf, handleArbitrationExpenseModal, openArbitrationInfoModal, handleArbitrationEdit, handleFileCloseModal, isAssigningArbitration, isReleasingArbitration]);

    const hasCaseNumberSearch = caseNumberSearch && caseNumberSearch.trim().length >= 3;
    const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
    const hasAccidentDateSearch = accidentDateSearch && accidentDateSearch.trim().length > 0;
    const hasOtherSearches = hasTcIdSearch || hasAccidentDateSearch;

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row>
                        <Col md="3">
                            <FormGroup>
                                <Label>Esas Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Esas numarası ile ara... (en az 3 karakter)"
                                    value={caseNumberSearch}
                                    onChange={handleCaseNumberSearchChange}
                                    style={{ textTransform: 'uppercase' }}
                                    disabled={hasOtherSearches}
                                />
                                {caseNumberSearch && caseNumberSearch.trim().length > 0 && caseNumberSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                                {hasOtherSearches && (
                                    <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="TC kimlik numarası / Vergi numarası..."
                                    value={tcIdSearch}
                                    onChange={handleTcIdSearchChange}
                                    disabled={hasCaseNumberSearch}
                                    maxLength="11"
                                />
                                {tcIdSearch && tcIdSearch.trim().length > 0 && tcIdSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                                {hasCaseNumberSearch && (
                                    <small className="text-muted">Esas numarası araması aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Plaka Kaza Tarihi</Label>
                                <Input
                                    type="date"
                                    value={accidentDateSearch}
                                    onChange={handleAccidentDateSearchChange}
                                    disabled={hasCaseNumberSearch}
                                />
                                {hasCaseNumberSearch && (
                                    <small className="text-muted">Esas numarası araması aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="3">
                            <FormGroup>
                                <Label>Tahkim Durumu Filtrele</Label>
                                <Input
                                    type="select"
                                    value={selectedState}
                                    onChange={handleArbitrationStateFilterChange}
                                    disabled={hasCaseNumberSearch || hasOtherSearches}
                                >
                                    <option value="">Tümü</option>
                                    {arbitrationStates.map(state => (
                                        <option key={state.id} value={state.id}>
                                            {state.title}
                                        </option>
                                    ))}
                                </Input>
                                {(hasCaseNumberSearch || hasOtherSearches) && (
                                    <small className="text-muted">Diğer aramalar aktifken kullanılamaz</small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={columns}
                        data={items}
                        progressPending={loading}
                        pagination
                        paginationServer
                        stickyActionPosition="right"
                        paginationTotalRows={totalRows}
                        onChangePage={setPage}
                        onChangeRowsPerPage={(newPageSize, newPage) => {
                            setPageSize(newPageSize);
                            setPage(newPage);
                        }}
                        paginationPerPage={pageSize}
                        paginationDefaultPage={page}
                        selectableRows={false}
                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                        noDataComponent={loading ? null : "Tahkimde bulunan dosya bulunamadı"}
                    />
                </Col>
            </Row>

            <Modal isOpen={isArbitrationInfoModalOpen} toggle={closeArbitrationInfoModal} size="lg">
                <ModalHeader toggle={closeArbitrationInfoModal}>Tahkim Bilgileri Gir</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationApplicationDate">Başvuru Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="datetime-local"
                                    id="arbitrationApplicationDate"
                                    value={arbitrationApplicationDate}
                                    onChange={(e) => setArbitrationApplicationDate(e.target.value)}
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationCaseNumber">Esas Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="arbitrationCaseNumber"
                                    value={arbitrationCaseNumber}
                                    onChange={(e) => setArbitrationCaseNumber(e.target.value)}
                                    placeholder="Esas numarasını giriniz..."
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="arbitrationPriceForNow">Şimdilik Tutarı</Label>
                                <Input
                                    type="text"
                                    id="arbitrationPriceForNow"
                                    value={displayDecimal(arbitrationPriceForNow)}
                                    onChange={(e) => setArbitrationPriceForNow(normalizeFieldValue('arbitrationPriceForNow', e.target.value))}
                                    placeholder="Örn: 1000,50"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{ color: 'secondary', onClick: closeArbitrationInfoModal }}>
                        İptal
                    </Btn>
                    <Btn
                        attrBtn={{
                            color: 'primary',
                            onClick: handleSubmitArbitrationInfo,
                            disabled: isSubmittingArbitrationInfo || !arbitrationApplicationDate || !arbitrationCaseNumber
                        }}
                    >
                        {isSubmittingArbitrationInfo ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Kaydediliyor...
                            </>
                        ) : (
                            'Kaydet'
                        )}
                    </Btn>
                </ModalFooter>
            </Modal>

            {currentArbitrationItem && (
                <ExpenseDataModal
                    apiEndpoint={process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense'}
                    isOpen={isArbitrationExpenseModalOpen}
                    toggle={closeArbitrationExpenseModal}
                    mode="add"
                    selectedExpense={null}
                    refreshData={fetchArbitrationActiveData}
                    customerId={null}
                    autoAssignCustomer={false}
                    accidentFile={[]}
                    callformId={null}
                    readOnlyView={false}
                    relatedArbitrationSchema={currentArbitrationItem.arbitrationInformation?.id ? [String(currentArbitrationItem.arbitrationInformation.id)] : []}
                    arbitrationSchemaType="TAHKIM_BASVURU_UCRETI_ODEMESI"
                    defaultAmount={currentArbitrationItem.arbitrationInformation?.arbitrationApplicationFee}
                />
            )}

            <FileCloseModal
                isOpen={isFileCloseModalOpen}
                toggle={closeFileCloseModal}
                arbitrationId={currentArbitrationIdForClose}
                refreshArbitrationData={fetchArbitrationActiveData}
            />

            {/* Tahsilat Listesi Modalı */}
            <PaymentCollectionListModal
                isOpen={isPaymentCollectionListModalOpen}
                toggle={closePaymentCollectionListModal}
                title={`Tahsilatlar${currentAccidentFileForPayments?.fileNumber ? ` - Dosya ${currentAccidentFileForPayments.fileNumber}` : ''}`}
                paymentCollections={paymentCollections}
                isLoading={isPaymentCollectionsLoading}
                submittingId={finalCompensationSubmittingId}
                onEdit={handleEditPaymentCollection}
                onSendToCover={handleSendPaymentCollectionToCover}
                onAdd={handleAddPaymentCollection}
                isSendToCoverDisabled={(collection, isSubmitting) => isPaymentCollectionsLoading || !currentAccidentFileForPayments?.id || isSubmitting}
            />

            {/* Tahsilat Ekle/Düzenle Modalı */}
            <PaymentCollectionDataModal
                apiEndpoint={paymentCollectionApiUrl}
                isOpen={paymentCollectionModalOpen}
                toggle={togglePaymentCollectionModal}
                mode={paymentCollectionModalMode}
                selectedPaymentCollection={selectedPaymentCollection}
                refreshData={refreshPaymentCollections}
                accidentFile={currentAccidentFileForPayments?.id ? [String(currentAccidentFileForPayments.id)] : []}
                callForm={currentAccidentFileForPayments?.callForm
                    ? (Array.isArray(currentAccidentFileForPayments.callForm)
                        ? currentAccidentFileForPayments.callForm.map(String)
                        : [String(currentAccidentFileForPayments.callForm)])
                    : []}
                enforcement={[]}
            />
        </Container>
    );
};

export default TodoTabArbitrationActive;
